import { requestBuilder } from '../../http/RequestBuilder';
import type { GeneralResponse } from '../../http/types/Common';
import type {
    AdminICTDailySummaryRowDto,
    AdminICTReconNotFoundDto,
    AdminICTReconStatusDto,
    AdminICTSnapshotComparisonDto,
    AdminICTTransfersDto,
    AdminICTWithdrawalRequestRow,
    CancelICTTransferRequestDto,
    CancelICTTransferResponseDto,
    ManualResolutionRequestDto,
    PatchICTWithdrawalRequestBody,
    ReconMutationResponseDto,
    SendWireEmailRequestDto,
    TriggerBatchReconRequestDto,
    TriggerBatchReconResponseDto,
    UploadReconFileRequestDto,
    UploadToSftpRequestDto,
} from '../types/ImOpsTypes';

/**
 * Get detailed ICT transfers for a specific settlement date
 * @example GET /api/admin/ict/transfers/2026-01-13
 */
export const useGetIctTransfers = requestBuilder.query<
    AdminICTTransfersDto,
    { settlementDate: string }
>({
    endpoint: ({ settlementDate }) =>
        `/api/admin/ict/transfers/${settlementDate}`,
    queryKey: ({ settlementDate }) =>
        `/api/admin/ict/transfers/${settlementDate}`,
});

/**
 * Get daily summary for a date range
 * @example GET /api/admin/ict/daily-summary?from_date=2026-01-13&to_date=2026-01-15
 */
export const useGetIctDailySummary = requestBuilder.query<
    AdminICTDailySummaryRowDto[],
    { fromDate: string; toDate: string }
>({
    endpoint: ({ fromDate, toDate }) =>
        `/api/admin/ict/daily-summary?fromDate=${fromDate}&toDate=${toDate}`,
    queryKey: ({ fromDate, toDate }) =>
        `/api/admin/ict/daily-summary/${fromDate}/${toDate}`,
});

/**
 * Compare snapshots across Apex, Synctera, and FF for a settlement date
 * @example GET /api/admin/ict/snapshot-comparison/2026-01-21
 */
export const useGetIctSnapshotComparison = requestBuilder.query<
    AdminICTSnapshotComparisonDto,
    { settlementDate: string }
>({
    endpoint: ({ settlementDate }) =>
        `/api/admin/ict/snapshot-comparison/${settlementDate}`,
    queryKey: ({ settlementDate }) =>
        `/api/admin/ict/snapshot-comparison/${settlementDate}`,
});

/**
 * Get reconciliation record for a specific settlement date.
 * Returns the ICTBatchReconciliation record or 404 if none exists.
 * @example GET /api/admin/ict/recon-status/2026-01-13
 */
export const useGetIctReconStatus = requestBuilder.query<
    AdminICTReconStatusDto | AdminICTReconNotFoundDto,
    { settlementDate: string }
>({
    endpoint: ({ settlementDate }) =>
        `/api/admin/ict/recon-status/${settlementDate}`,
    queryKey: ({ settlementDate }) =>
        `/api/admin/ict/recon-status/${settlementDate}`,
});

/**
 * Cancel an ICT transfer (deposit or withdrawal)
 * @example POST /api/admin/ict/cancel
 */
export const useCancelIctTransfer = requestBuilder.mutation<
    CancelICTTransferResponseDto,
    CancelICTTransferRequestDto
>({
    endpoint: '/api/admin/ict/cancel',
    method: 'POST',
});

/**
 * Manually trigger ICT batch reconciliation for a specific settlement date
 * @example POST /api/admin/ict/trigger-batch-recon
 */
export const useTriggerBatchRecon = requestBuilder.mutation<
    TriggerBatchReconResponseDto,
    TriggerBatchReconRequestDto
>({
    endpoint: '/api/admin/ict/trigger-batch-recon',
    method: 'POST',
});

/**
 * Mark wire email as sent for a settlement date (ops send from Gmail).
 * @example POST /api/admin/ict/send-wire-email
 */
export const useSendWireEmail = requestBuilder.mutation<
    ReconMutationResponseDto,
    SendWireEmailRequestDto
>({
    endpoint: '/api/admin/ict/send-wire-email',
    method: 'POST',
});

/**
 * Upload reconciliation file to Apex SFTP
 * @example POST /api/admin/ict/upload-to-apex-sftp
 */
export const useUploadToApexSftp = requestBuilder.mutation<
    ReconMutationResponseDto,
    UploadToSftpRequestDto
>({
    endpoint: '/api/admin/ict/upload-to-apex-sftp',
    method: 'POST',
});

/**
 * Upload reconciliation file to Synctera SFTP
 * @example POST /api/admin/ict/upload-to-synctera-sftp
 */
export const useUploadToSyncteraSftp = requestBuilder.mutation<
    ReconMutationResponseDto,
    UploadToSftpRequestDto
>({
    endpoint: '/api/admin/ict/upload-to-synctera-sftp',
    method: 'POST',
});

/**
 * Upload an edited recon file to S3 (replaces the existing file at the same key).
 * @example POST /api/admin/ict/upload-recon-file
 */
export const useUploadReconFile = requestBuilder.mutation<
    ReconMutationResponseDto,
    UploadReconFileRequestDto
>({
    endpoint: '/api/admin/ict/upload-recon-file',
    method: 'POST',
});

/**
 * Manually resolve a reconciliation record
 * @example POST /api/admin/ict/manual-resolution
 */
export const useManualResolution = requestBuilder.mutation<
    ReconMutationResponseDto,
    ManualResolutionRequestDto
>({
    endpoint: '/api/admin/ict/manual-resolution',
    method: 'POST',
});

/**
 * Build the presigned download URL for a recon file
 */
export const getReconFileDownloadUrl = (settlementDate: string): string =>
    `/api/admin/ict/recon-file/${settlementDate}`;

export const ICT_WITHDRAWAL_REQUESTS_QUERY_KEY =
    '/api/admin/ict-withdrawal-requests';

/**
 * List manual ICT withdrawal requests (admin).
 * @example GET /api/admin/ict-withdrawal-requests
 */
export const useGetICTWithdrawalRequests = requestBuilder.query<
    AdminICTWithdrawalRequestRow[]
>({
    endpoint: '/api/admin/ict-withdrawal-requests',
    queryKey: ICT_WITHDRAWAL_REQUESTS_QUERY_KEY,
});

/**
 * Update a manual ICT withdrawal request (e.g. advance status).
 * @example PATCH /api/admin/ict-withdrawal-requests/:id
 */
export const usePatchICTWithdrawalRequest = requestBuilder.mutation<
    AdminICTWithdrawalRequestRow,
    PatchICTWithdrawalRequestBody,
    { id: string }
>({
    endpoint: ({ id }) =>
        `/api/admin/ict-withdrawal-requests/${encodeURIComponent(id)}`,
    method: 'PATCH',
});

/**
 * Delete a manual ICT withdrawal request (admin).
 * @example DELETE /api/admin/ict-withdrawal-requests/:id
 */
export const useDeleteICTWithdrawalRequest = requestBuilder.mutation<
    GeneralResponse,
    null,
    { id: string }
>({
    endpoint: ({ id }) =>
        `/api/admin/ict-withdrawal-requests/${encodeURIComponent(id)}`,
    method: 'DELETE',
});
